import { observer } from 'mobx-react-lite';
import InfoBarMessagePart from './InfoBarMessagePart';
import {
    MessagePart,
    InfoBarMessageColor,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import getInfoBarIcon from 'owa-info-bar/lib/utils/getInfoBarIcon';
import getInfoBarIconColor from 'owa-info-bar/lib/utils/getInfoBarIconColor';
import { default as isLocalizedInfoBarMessage } from 'owa-info-bar/lib/utils/isLocalizedInfoBarMessage';
import { Icon } from '@fluentui/react/lib/Icon';
import { generateDomPropertiesForAria } from 'owa-accessibility';
import { logUsage } from 'owa-analytics';
import { formatToArray } from 'owa-localize';
import * as React from 'react';

const styles = require('./InfoBarMessage.scss');
const classNames = require('classnames/bind').bind(styles);

export default observer(function InfoBarMessage(props: InfoBarMessageViewState) {
    const renderContent = (viewState: InfoBarMessageViewState): JSX.Element => {
        return (
            <>
                {viewState.messageElement
                    ? React.cloneElement(viewState.messageElement, {
                          colorScheme: viewState.colorScheme,
                      })
                    : isLocalizedInfoBarMessage(viewState)
                    ? renderLocalizedMessage(viewState)
                    : renderMessageParts(viewState.messageParts)}
            </>
        );
    };
    const renderLocalizedMessage = (viewState: InfoBarMessageViewState): JSX.Element => {
        return (
            <>
                {formatToArray(
                    viewState.localizedMessageTemplateString,
                    ...renderMessageParts(viewState.messageParts)
                )}
            </>
        );
    };
    const renderMessageParts = (parts: MessagePart[]): JSX.Element[] => {
        return parts?.map((part, index) => {
            return renderMessagePart(part, index);
        });
    };
    const renderMessagePart = (part: MessagePart, index: number): JSX.Element => {
        if (part) {
            return (
                <span key={index}>
                    <InfoBarMessagePart
                        messagePart={part}
                        colorScheme={props.colorScheme}
                        isMessageSemibold={props.isMessageSemibold}
                    />
                    {index != props.messageParts.length - 1 ? ' ' : null}
                </span>
            );
        } else {
            // log empty case datapoint to figure out which message id has this issue and return null
            logUsage('InfoBarMessagePartEmptyCase', [props.key]);
            return null;
        }
    };
    const { rank, color, textColor, ariaProps, icon } = props;
    const textColorStyle = {
        red: textColor == InfoBarMessageColor.Red,
        green: textColor == InfoBarMessageColor.Green,
        yellow: textColor == InfoBarMessageColor.Yellow,
        orange: textColor == InfoBarMessageColor.Orange,
        green20: textColor == InfoBarMessageColor.Green20,
    };
    const infoBarIconColor = getInfoBarIconColor(rank, color);
    const iconName = getInfoBarIcon(rank);
    const textContainerStyle = classNames(
        styles.textContainer,
        styles.textContainerMarginForNewInfoBarUI,
        props.textContainerClassName,
        textColorStyle
    );
    if (icon) {
        return (
            <div
                key="infoBarMessageDiv"
                className={classNames(styles.container, styles.newInfoBarUI)}
                {...generateDomPropertiesForAria(ariaProps)}>
                <img className={classNames(styles.addinIcon, infoBarIconColor)} src={icon} />
                <div className={textContainerStyle}>{renderContent(props)}</div>
            </div>
        );
    } else {
        return (
            <div
                key="infoBarMessageDiv"
                className={classNames(styles.container, styles.newInfoBarUI)}
                {...generateDomPropertiesForAria(ariaProps)}>
                <Icon iconName={iconName} className={classNames(styles.icon, infoBarIconColor)} />
                <div className={textContainerStyle}>{renderContent(props)}</div>
            </div>
        );
    }
});
